import React from "react";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "@directv-digital/dds.ui.dtv-text";
import Button from "@directv-digital/fusion.atoms.button";
import { EqualHeightElement } from "react-equal-height/clean";
import { PricingSectionProps } from "../../flip-cards.props";
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";

const styles = {
  container: {
    display: "flex",
    gap: "8px"
  },
  priceWrapper: {
    display: "flex",
    flexDirection: "column"
  }
};

const PricingSection = (props: PricingSectionProps) => {
  const { strikeThroughPrice, priceText, cta, mobileImagePosition, cardType, cardName, isMobile } = props;

  const shopAriaLabel = cta?.label && cardName ? `${cta.label} ${cardName}` : cta?.label;

  // Screen-reader label for the whole pricing group (added on devdeploy/next).
  const pricingAriaLabel = [
    strikeThroughPrice?.content ? `Regular price ${strikeThroughPrice.content}` : '',
    priceText?.content ? `Monthly price ${priceText.content}${priceText?.spanText?.content || ''}` : ''
  ].filter(Boolean).join(', ');

  // Mobile (xs ≡ < 600px) layout variants expressed as sx breakpoints so SSR,
  // hydration and resize resolve in CSS — no isMobile re-render needed.
  const isTopImage = mobileImagePosition === "top";
  const mobileRowLayout = cardType === "back" || isTopImage;
  const xsAlignItems = isTopImage ? "center" : (cardType === "back" ? "flex-end" : "flex-start");

  const containerSx = {
    ...styles.container,
    flexDirection: {
      xs: mobileRowLayout ? "row" : "column",
      sm: "column",
      md: "row"
    },
    justifyContent: {
      // Both card types use space-between at xs; the flex-start fallback
      // only guards a (currently unused) cardType-less render.
      xs: cardType ? "space-between" : "flex-start",
      sm: cardType === "front" ? "flex-end" : "flex-start",
      md: "space-between"
    },
    alignItems: {
      xs: xsAlignItems,
      sm: "flex-start",
      md: "flex-end"
    }
  };

  try {
    return (
        <DtvBox role="group" aria-label={pricingAriaLabel || 'Pricing'} sx={containerSx}>
          {/* strikethrough & offer price */}
          <DtvBox sx={styles.priceWrapper}>
            {/* Strikethrough / label row, equalized across ALL cards so the main price
                below it starts at the same Y whether or not the card has a strikethrough
                or a "Starting at" label. Always rendered (empty when absent) so EqualHeight
                reserves the row height for cards that have no strikethrough. */}
            <EqualHeightElement name="card-strikethrough" disable={isMobile}>
              <DtvBox>
                {strikeThroughPrice?.content && (
                  <>
                    {/* SR-only context: strikethrough is visual-only; announce "Regular Monthly price" before the price (WCAG 1.3.1). Uses the modern visually-hidden recipe (clipPath + margin:-1px) to keep the 1px node out of layout flow. Trailing space ensures SRs separate the label from the price. */}
                    <DtvBox
                      component="span"
                      sx={{
                        position: "absolute",
                        width: "1px",
                        height: "1px",
                        padding: 0,
                        margin: "-1px",
                        overflow: "hidden",
                        clipPath: "inset(50%)",
                        whiteSpace: "nowrap",
                        border: 0
                      }}
                    >
                      {"Regular Monthly price "}
                    </DtvBox>
                    <DtvText
                      {...strikeThroughPrice}
                      sx={{
                        ...(strikeThroughPrice?.textDecoration && { textDecoration: strikeThroughPrice.textDecoration })
                      }}
                    />
                  </>
                )}
              </DtvBox>
            </EqualHeightElement>
              {priceText?.content && (
                <DtvBox>
                  <DtvText {...priceText} component="span" aria-label={`Monthly price ${priceText.content}`} sx={{
 fontSize: {
 xs: "24px",
md: "28px"
}
}} />
                  <DtvText {...priceText?.spanText} content={priceText?.duration ?? priceText?.spanText?.content} component="span" {...(priceText?.spanText?.content ? { 'aria-label': priceText.spanText.content } : {})} sx={{ fontSize: "14px !important" }} />
                </DtvBox>
              )}
            </DtvBox>
          {/* shop cta */}
          {cta && (
            <Button
              href={cta?.href}
              aria-label={shopAriaLabel}
              mode={cta?.mode || "light"}
              size={cta?.size || "large"}
              buttontype={cta?.buttontype || (cta as any)?.buttonType || "primary"}
              tabIndex={0}
              googleanalyticsdata={getAnalyticsAttrs(shopAriaLabel, "panel_product cards", cta?.href)}
              sx={{
                m: "0",
                "& span": {
                  fontFamily: "roboto-regular !important"
                },
                color: cta?.color,
                background: cta?.background,
                "&:hover, &:active, &:focus": {
                  color: cta?.hover?.color,
                  background: cta?.hover?.background,
                  border: cta?.hover?.border
                }
              }}
            >
              {cta?.label}
            </Button>
          )}
        </DtvBox>
    );
  } catch (error) {
    return <ErrorDisplay error={error} component="PricingSection" />;
  }
};

export default React.memo(PricingSection);
